import type {AxiosError} from "axios";

import type {HouseholdDeactivatePreview} from "../types/household-status";

type DeactivateErrorBody = {
  message?: string;
  code?: string;
  error_code?: string;
  preview?: HouseholdDeactivatePreview;
};

export class HouseholdDeactivateConfirmationError extends Error {
  readonly preview: HouseholdDeactivatePreview;

  constructor(preview: HouseholdDeactivatePreview, message?: string) {
    super(message ?? "Confirmation required to deactivate this household.");
    this.name = "HouseholdDeactivateConfirmationError";
    this.preview = preview;
  }
}

export function parseHouseholdDeactivateConfirmationError(
  error: unknown,
): HouseholdDeactivateConfirmationError | null {
  const axiosError = error as AxiosError<DeactivateErrorBody>;
  if (axiosError.response?.status !== 409) return null;

  const body = axiosError.response.data;
  const code = body?.code ?? body?.error_code;
  if (code !== "confirmation_required" || !body?.preview) return null;

  return new HouseholdDeactivateConfirmationError(body.preview, body.message);
}

type AddressErrorBody = {
  message?: string;
  error_code?: string;
  data?: {house_hold_id?: number};
};

export function getHouseholdAddressErrorMessage(error: unknown): string | null {
  const axiosError = error as AxiosError<AddressErrorBody>;
  if (axiosError.response?.status !== 409) return null;

  const errorCode = axiosError.response.data?.error_code;
  if (
    errorCode !== "household_address_conflict" &&
    errorCode !== "household_address_deactivated_conflict"
  ) {
    return null;
  }

  return (
    axiosError.response.data?.message ??
    "This address is already in use at this property."
  );
}

export function getHouseholdDeactivatedErrorMessage(error: unknown): string | null {
  const axiosError = error as AxiosError<AddressErrorBody>;
  if (axiosError.response?.status !== 409) return null;
  if (axiosError.response.data?.error_code !== "household_deactivated") return null;

  return (
    axiosError.response.data?.message ??
    "This household is deactivated. Reactivate it before making changes."
  );
}

type DeleteBlockedErrorBody = {
  message?: string;
  error_code?: string;
  data?: {blocked_by?: string[]};
};

export function getHouseholdDeleteBlockedErrorMessage(error: unknown): string | null {
  const axiosError = error as AxiosError<DeleteBlockedErrorBody>;
  if (axiosError.response?.status !== 403) return null;
  if (axiosError.response.data?.error_code !== "household_delete_blocked") return null;

  return (
    axiosError.response.data?.message ?? "This household cannot be deleted."
  );
}
